import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, FileText, Calculator, Briefcase, TrendingUp, RefreshCw,
  Building, Users, Zap, Clock, Phone, Mail, Shield, BarChart, Award,
  DollarSign, Landmark, HeartHandshake, Target
} from 'lucide-react';

const BusinessLoan = () => {
  const [principal, setPrincipal] = useState(1000000);
  const [interest, setInterest] = useState(12.5);
  const [tenure, setTenure] = useState(5);

  const calculateEMI = () => {
    const p = principal;
    const r = interest / 12 / 100;
    const n = tenure * 12;
    const emi = p * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    return isNaN(emi) ? 0 : Math.round(emi);
  };

  const emi = calculateEMI();
  const totalPayable = emi * tenure * 12;
  const totalInterest = totalPayable - principal;

  return (
    <div className="bg-gray-50">
      <div className="mb-6">
        <div className="flex justify-center lg:justify-end">
          <img
            src="Business.png"
            alt="Business Loan"
            className="max-w-full h-full"
          />
        </div>
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-16 max-w-4xl mx-auto text-center animate-fade-up">
          <h2 className="text-3xl font-bold text-green-700 mb-6">About Business Loan</h2>
          <p className="text-gray-700 text-lg leading-relaxed">
            A business loan is a versatile financial tool designed to fuel the growth and sustainability of your enterprise. Whether you are a startup, a growing MSME, or an established corporation,  CashPro Financial Services helps you secure the right funding—be it for working capital, expansion, equipment purchase, or managing cash flow. With flexible tenures, competitive interest rates, and minimal documentation, we connect you with lenders who understand your business needs.
          </p>
        </div>
        <div className="mb-16 bg-gradient-to-r from-green-50 to-green-50 rounded-xl p-8 animate-fade-up animation-delay-200">
          <p className="text-lg text-gray-700 mb-6">
            Use our business loan EMI calculator to estimate your monthly payments. Adjust the loan amount, interest rate, and tenure to see the EMI, total interest payable, and total payment. This helps you plan your finances and choose a repayment structure that suits your cash flow.
          </p>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1">
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Loan Amount: ₹{principal.toLocaleString()}</label>
                  <input
                    type="range"
                    min="50000"
                    max="50000000"
                    step="50000"
                    value={principal}
                    onChange={(e) => setPrincipal(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Interest Rate: {interest}% p.a.</label>
                  <input
                    type="range"
                    min="9"
                    max="18"
                    step="0.1"
                    value={interest}
                    onChange={(e) => setInterest(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Tenure: {tenure} years</label>
                  <input
                    type="range"
                    min="1"
                    max="7"
                    step="1"
                    value={tenure}
                    onChange={(e) => setTenure(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
            <div className="flex-1 bg-white p-6 rounded-xl shadow-md text-center">
              <Calculator className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Estimated Monthly EMI</p>
              <p className="text-4xl font-bold text-green-700">₹{emi.toLocaleString()}</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>Total Interest Payable: ₹{totalInterest.toLocaleString()}</p>
                <p>Total Payment: ₹{totalPayable.toLocaleString()}</p>
              </div>
              <button className="mt-4 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-full transition duration-300">
                Apply Now
              </button>
            </div>
          </div>
        </div>
        <div className="mb-16 text-center animate-fade-up animation-delay-400">
          <p className="text-lg font-semibold text-green-700 bg-green-50 inline-block px-6 py-3 rounded-full shadow-sm">
            Business Loan Interest Rate - Starting from 11.25%*<br />
            We compare and offer the best interest rate possible!
          </p>
        </div>
        <div className="mb-16 bg-white rounded-xl shadow-md p-8 animate-fade-up animation-delay-600">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Eligibility Criteria:</h2>
          <p className="text-gray-600 mb-6 text-center">
            Eligibility for business loans varies by lender, but the following criteria are commonly applied:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Nationality: Indian Residents, NRIs, and PIOs (subject to lender policies)",
              "Business Vintage: Minimum 3 years of business existence (some lenders offer loans after 1 year for startups with strong potential)",
              "Annual Turnover: At least ₹10 lakhs (varies by lender and loan amount)",
              "Profitability: Business should be profitable for at least 2 years (exceptions for high-growth startups)",
              "Credit Score: Preferably 700 and above; higher scores unlock better rates and faster approvals",
              "Age Limit: 21 – 65 years for proprietors/partners/directors",
              "Documents: IT returns, bank statements, financials, and KYC of the business and owners"
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 animate-fade-up animation-delay-800">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Business Loan Types:</h2>
          <div className="space-y-6">
            {[
              {
                icon: Briefcase,
                title: "Working Capital Loan",
                desc: "A short‑term loan to cover day‑to‑day operational expenses like salaries, rent, inventory purchases, and raw materials. Ideal for managing seasonal fluctuations, bridging receivables gaps, and maintaining smooth cash flow without disrupting business momentum."
              },
              {
                icon: Building,
                title: "Term Loan",
                desc: "A lump sum amount repaid over a fixed tenure (1‑7 years). Perfect for long‑term investments such as purchasing machinery, expanding production capacity, setting up new branches, or upgrading technology. Offers structured repayment with fixed EMIs."
              },
              {
                icon: TrendingUp,
                title: "Business Expansion Loan",
                desc: "Designed for scaling operations—entering new markets, launching new product lines, increasing marketing budgets, or acquiring other businesses. Provides the capital needed to seize growth opportunities without diluting equity."
              },
              {
                icon: RefreshCw,
                title: "Loan Against Property (LAP) for Business",
                desc: "A secured loan where you pledge commercial or residential property as collateral. Typically offers higher loan amounts (up to 70‑80% of property value) and lower interest rates compared to unsecured loans. Ideal for large‑scale funding needs."
              },
              {
                icon: Shield,
                title: "Government‑Subsidized Loans (MSME / Startup)",
                desc: "Loans under schemes like MUDRA, CGTMSE, Startup India, or state‑specific initiatives that provide collateral‑free funding with interest subvention. CashPro Financial Services helps you identify and apply for these schemes, maximizing your benefits."
              },
              {
                icon: Zap,
                title: "Equipment / Machinery Loan",
                desc: "Finance for purchasing new or used machinery, vehicles, or technology upgrades. The equipment itself often serves as collateral, making approval easier. Helps modernize operations and improve efficiency."
              },
              {
                icon: BarChart,
                title: "Invoice / Bill Discounting",
                desc: "A facility where you receive immediate cash against unpaid invoices. Helps bridge the gap between invoicing and payment receipt, improving liquidity without taking on additional debt."
              },
              {
                icon: Users,
                title: "Business Loan for Women Entrepreneurs",
                desc: "Tailored loans for women‑owned businesses, often with lower interest rates, relaxed eligibility, and special government incentives. We help you access these benefits and navigate the application process."
              },
              {
                icon: DollarSign,
                title: "Startup Business Loan",
                desc: "Specially designed for new ventures (under 3 years old) that may not have a long financial history. Lenders evaluate the business plan, founder’s profile, and industry potential. Flexible terms and mentorship support may be included."
              },
              {
                icon: Landmark,
                title: "Professional Loan",
                desc: "For doctors, chartered accountants, architects, and other professionals. These loans are unsecured and cater to practice expansion, equipment purchase, or working capital needs. Often come with quick approvals and minimal documentation."
              }
            ].map((type, idx) => (
              <div key={idx} className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0">
                <type.icon className="w-8 h-8 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-1">{type.title}</h3>
                  <p className="text-gray-600">{type.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 bg-white rounded-xl shadow-md p-8 animate-fade-up animation-delay-1000">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Documents Required</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "KYC Documents: PAN Card, Aadhaar Card, Passport/Voter ID of all partners/directors/proprietor (self‑attested copies)",
              "Proof of Business Existence: Registration certificate (GST, MSME, Udyam), Partnership deed, MOA/AOA, or Shop & Establishment certificate",
              "Financial Documents: IT Returns (last 3 years), Audited Balance Sheet & Profit & Loss Statement (last 3 years) – with CA certification",
              "Bank Statements: Last 12 months’ bank statements of the business (showing cash flow and turnover)",
              "Proof of Business Address: Utility bill, lease agreement, property tax receipt, or rent receipt with NOC",
              "Loan‑Specific Documents: Quotations for equipment (if applying for equipment loan), invoices for discounting, property papers (for LAP), business plan (for startups), etc.",
              "Additional Documents for Professional Loans: Degree certificate, professional registration (e.g., medical council, ICAI, COA), practice license"
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center animate-fade-up animation-delay-1200">
          <h2 className="text-3xl font-bold text-green-700 mb-4">We are Here to Help You</h2>
          <p className="text-gray-600 mb-6">
             CashPro Financial Services provides unbiased business loan consulting, comparing offers from multiple lenders to secure the best terms. Our dedicated team understands the unique challenges of businesses and guides you through documentation, eligibility checks, and disbursal—all at zero cost to you. Let us help you unlock the capital your business deserves.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">APPLY FOR LOAN</p>
              <p className="text-gray-800 font-medium mt-1">Looking for a Business Loan?</p>
              <button className="mt-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-full transition duration-300 shadow">
                Apply Now
              </button>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">CALL US AT</p>
              <a href="tel:+918887141052" className="text-lg font-bold text-green-600 hover:underline">
                +91 88871 41052
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">MAIL US AT</p>
              <a href="mailto:info@cashpro.co.in" className="text-lg font-bold text-green-600 hover:underline">
                info@cashpro.co.in
              </a>
            </div>
          </div>
          <Link to="/contact" className="inline-block text-green-600 hover:underline font-medium">
            Contact Us →
          </Link>
        </div>
      </div>
      <style>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-600 {
          animation-delay: 0.6s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-800 {
          animation-delay: 0.8s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-1200 {
          animation-delay: 1.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
};

export default BusinessLoan;